import { useEffect, useState } from 'react';
import type { ValueSetCode } from '../ValueSetCombobox';
import { freeTextItem, splitSelection, type DrugSearchSelection } from './drugSearchSelection';
import { formulationKey, isManualEntry } from './formulationSearch';
import { MedicationSearchStep } from './MedicationSearchStep';
import { productFromFormulation } from './medicationProduct';
import type { MedicationSearchQueries, PrescribableMedication } from './types';
import { useFormulationSearch } from './useFormulationSearch';

export interface MedicationProductSearchFieldProps {
  /** Fires with the product for the form's `product` field: an RxNorm-coded formulation, a
   * free-text sentinel `ValueSetCode` (see `isFreeTextItem` — map it to `{ text }` on submit via
   * `medicationInputFromProduct`), or null before a medication settles.
   *
   * `sigLine` carries the chosen formulation's first ScriptSure sig template, when it has one — the
   * form derives Dose/Units/Route from it. Absent for free-text picks and RxNorm-fallback rows,
   * which have no vendor sig. */
  onValueChange: (value: ValueSetCode | null, sigLine?: string) => void;
  queries: MedicationSearchQueries;
}

/**
 * The ScriptSure product picker as a self-contained form field: the two-step drug → strength/form
 * drill-down (with the RxNorm-catalog fallback), auto-selecting the first formulation, degrading to
 * a free-text sentinel product for an explicit free-text pick or a drug with no dispensable
 * formulation anywhere. Emits a `ValueSetCode` compatible with the form's existing `product` field,
 * so everything downstream (dose-form derivation, submit) is unchanged.
 */
export function MedicationProductSearchField({
  onValueChange,
  queries,
}: MedicationProductSearchFieldProps): React.ReactNode {
  const [searchSelection, setSearchSelection] = useState<DrugSearchSelection | null>(null);
  const [selectedFormulation, setSelectedFormulation] = useState<PrescribableMedication | null>(null);

  const { selectedDrug, freeTextName } = splitSelection(searchSelection);

  const {
    formulations: availableFormulations,
    isLoading: formulationsLoading,
    settled: formulationsSettled,
  } = useFormulationSearch(selectedDrug, queries);

  // Auto-select the first formulation once they load.
  useEffect(() => {
    if (!selectedFormulation && availableFormulations.length > 0) {
      setSelectedFormulation(availableFormulations[0] ?? null);
    }
  }, [availableFormulations, selectedFormulation]);

  // Manual entry: an explicit free-text pick, or a chosen drug whose formulation lookups
  // (ScriptSure AND the RxNorm-catalog fallback) both settled empty.
  const manualEntry =
    freeTextName !== null || isManualEntry(Boolean(selectedDrug), formulationsSettled, availableFormulations.length);

  const product = selectedFormulation ? productFromFormulation(selectedDrug?.name, selectedFormulation) : null;
  const manualName = freeTextName ?? selectedDrug?.name;

  // Report the resolved product up whenever it changes: the coded formulation, the free-text
  // sentinel, or null while unsettled. Deps track primitives, not the derived object.
  const productCode = product?.code;
  // ScriptSure returns one sig template per saved format; the first is its canonical directions.
  const sigLine = selectedFormulation?.sigs?.[0]?.sigLine;
  useEffect(() => {
    if (product) {
      onValueChange(product, sigLine);
    } else if (manualEntry && manualName) {
      onValueChange(freeTextItem(manualName));
    } else {
      onValueChange(null);
    }
    // `onValueChange` may be recreated every render (e.g. inside a form-field render prop), so it is
    // deliberately excluded: the effect fires only when the primitive selection state changes, and
    // then runs with that render's (current) callback. `product` and `sigLine` are both derived from
    // those primitives — the sig belongs to the selected formulation, so it can't change without
    // `productCode` changing too.
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [productCode, manualEntry, manualName]);

  const handleSelectionChange = (selection: DrugSearchSelection | null): void => {
    setSearchSelection(selection);
    setSelectedFormulation(null);
  };

  const handleFormulationChange = (code: string): void => {
    setSelectedFormulation(availableFormulations.find((m) => formulationKey(m) === code) ?? null);
  };

  return (
    <MedicationSearchStep
      selection={searchSelection}
      onSelectionChange={handleSelectionChange}
      formulations={availableFormulations}
      selectedCode={selectedFormulation ? formulationKey(selectedFormulation) : ''}
      onFormulationChange={handleFormulationChange}
      formulationsLoading={formulationsLoading}
      manualEntry={manualEntry}
      searchMedications={queries.searchMedications}
    />
  );
}
